import React, { useMemo, useState, Fragment } from 'react';
import { RowData, Inputs, PackageType, BuildPayType, PACKAGE_PRESETS, BONUS_DAY_15, BONUS_DAY_45, BONUS_DAY_90, PIPELINE_BUCKET_FEE, RECRUITER_MONTHLY_FEE, RECRUITER_BUILD_UPCHARGE, LEADER_CAMPAIGN_MONTHS, LEADER_CAMPAIGN_BUILD_UPCHARGE, LEADER_CAMPAIGN_CPH, LEADER_CAMPAIGN_MONTHLY_FEE, LEADER_ACTIVE_MONTHLY_FEE } from '../utils/roiCalculations';
import { fmtMoney } from '../utils/formatters';
const CAMPAIGN_SPEND_STEP = 2500;

// Build & implementation payment schedules per package. The first entry is the
// upfront payment in the Build column; remaining entries are consecutive
// monthly payments beginning in Month 2.
const BUILD_SPLIT_PLANS: Partial<Record<PackageType, number[][]>> = {
  advantage: [[1500, 1500, 1500]]
};
interface ProjectionTableProps {
  rows: RowData[];
  inputs: Inputs;
  onInputsChange: (inputs: Inputs) => void;
  halfSales: boolean;
  setHalfSales: (val: boolean) => void;
  doubleAttrition: boolean;
  setDoubleAttrition: (val: boolean) => void;
  onTarget: boolean;
  onToggleOnTarget: () => void;
  plusFive: boolean;
  onPlusFive: () => void;
  timeframe?: 3 | 6 | 12;
  setTimeframe?: (t: 3 | 6 | 12) => void;
}
export function ProjectionTable({
  rows,
  inputs,
  onInputsChange,
  halfSales,
  setHalfSales,
  doubleAttrition,
  setDoubleAttrition,
  onTarget,
  onToggleOnTarget,
  plusFive,
  onPlusFive,
  timeframe: timeframeProp,
  setTimeframe: setTimeframeProp
}: ProjectionTableProps) {
  const [tooltip, setTooltip] = useState<{
    content: string;
    x: number;
    y: number;
  } | null>(null);
  // Click popup for Monthly Fees breakdown
  const [feePopup, setFeePopup] = useState<{
    rowIdx: number;
    platformFee: number;
    campaignFee: number;
    recruiterFee: number;
    x: number;
    y: number;
  } | null>(null);
  // Click popup for the milestone bonus breakdown
  const [bonusPopup, setBonusPopup] = useState<{
    rowIdx: number;
    day15Reps: number;
    day45Reps: number;
    day90Reps: number;
    day15: number;
    day45: number;
    day90: number;
    leaderActive: number;
    x: number;
    y: number;
  } | null>(null);
  // Month 2 sales are directly editable during the leader campaign
  const [editingLeaderSales, setEditingLeaderSales] = useState(false);
  const [editLeaderSalesValue, setEditLeaderSalesValue] = useState<string>('');
  // Click popup for the Total Sales explanation
  const [salesPopup, setSalesPopup] = useState<{
    rowIdx: number;
    period: string;
    totalSales: number;
    rampingReps: number;
    veterans: number;
    x: number;
    y: number;
  } | null>(null);
  // Track which ad spend cell is being overridden with a custom amount
  const [editingAdSpend, setEditingAdSpend] = useState<number | null>(null);
  const [editAdSpendValue, setEditAdSpendValue] = useState<string>('');
  // Track which campaign cell is being edited
  const [editingCampaign, setEditingCampaign] = useState<number | null>(null);
  const [editCampaignValue, setEditCampaignValue] = useState<string>('');
  // Track which fee cells are being edited
  const [editingBuildFee, setEditingBuildFee] = useState<number | null>(null);
  const [editBuildFeeValue, setEditBuildFeeValue] = useState<string>('');
  const [editingPlatformFee, setEditingPlatformFee] = useState<number | null>(null);
  const [editPlatformFeeValue, setEditPlatformFeeValue] = useState<string>('');
  const [editingCampaignFee, setEditingCampaignFee] = useState<number | null>(null);
  const [editCampaignFeeValue, setEditCampaignFeeValue] = useState<string>('');
  // Track which metric row label is being hovered
  const [hoveredMetricKey, setHoveredMetricKey] = useState<string | null>(null);
  // Track which data cell is being hovered (for crosshair highlight)
  const [hoveredCell, setHoveredCell] = useState<{
    metricKey: string;
    colIdx: number;
  } | null>(null);
  // Timeframe state (controlled if props provided)
  const [timeframeInternal, setTimeframeInternal] = useState<3 | 6 | 12>(6);
  const timeframe = timeframeProp ?? timeframeInternal;
  const setTimeframe = setTimeframeProp ?? setTimeframeInternal;
  // Revenue shift state
  const [revenueShift, setRevenueShift] = useState<number>(0);
  // Sales delay state (number of months to zero out at the start)
  const [salesDelay, setSalesDelay] = useState<number>(0);
  // Collapsible section states
  const [expandedSections, setExpandedSections] = useState({
    recruiting: true,
    activeReps: true,
    investment: false,
    revenue: false
  });
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const showTooltip = (content: string, e: React.MouseEvent) => {
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    setTooltip({
      content,
      x: rect.right + 8,
      y: rect.top
    });
  };
  const hideTooltip = () => {
    setTooltip(null);
  };
  // When the leader campaign runs, the standard schedule is shifted out by two
  // months, so a displayed month maps back two slots in the input arrays.
  const leaderOffset = inputs.leaderCampaign ? LEADER_CAMPAIGN_MONTHS : 0;
  const isLeaderMonth = (monthIndex: number) => !!inputs.leaderCampaign && monthIndex >= 1 && monthIndex <= LEADER_CAMPAIGN_MONTHS;
  const inputIndexForMonth = (monthIndex: number) => monthIndex - 1 - leaderOffset;
  // Handle monthly campaign overrides
  const handleCampaignChange = (monthIndex: number, value: number) => {
    if (monthIndex === 0) return;
    const arrayIndex = inputIndexForMonth(monthIndex);
    if (arrayIndex < 0 || arrayIndex > 11) return;
    const newMonthlyCampaigns = [...(inputs.monthlyCampaigns || Array(12).fill(inputs.campaigns))];
    const newMonthlyAdSpend = [...(inputs.monthlyAdSpend || Array(12).fill(inputs.adSpend))];
    newMonthlyCampaigns[arrayIndex] = Math.max(0, value);
    // Editing the tier directly resets that month to the standard $2,500 per
    // campaign, clearing any custom spend override.
    newMonthlyAdSpend[arrayIndex] = CAMPAIGN_SPEND_STEP;
    onInputsChange({
      ...inputs,
      monthlyCampaigns: newMonthlyCampaigns,
      monthlyAdSpend: newMonthlyAdSpend
    });
  };
  const handlePushRight = (monthIndex: number) => {
    if (monthIndex === 0) return;
    const arrayIndex = inputIndexForMonth(monthIndex);
    if (arrayIndex < 0 || arrayIndex > 11) return;
    const currentMonthlyCampaigns = inputs.monthlyCampaigns || Array(12).fill(inputs.campaigns);
    const valueToPropagate = currentMonthlyCampaigns[arrayIndex];
    const currentMonthlyAdSpend = inputs.monthlyAdSpend || Array(12).fill(inputs.adSpend);
    const spendToPropagate = currentMonthlyAdSpend[arrayIndex];
    const newMonthlyCampaigns = currentMonthlyCampaigns.map((val, idx) => {
      return idx >= arrayIndex ? valueToPropagate : val;
    });
    const newMonthlyAdSpend = currentMonthlyAdSpend.map((val, idx) => {
      return idx >= arrayIndex ? spendToPropagate : val;
    });
    onInputsChange({
      ...inputs,
      monthlyCampaigns: newMonthlyCampaigns,
      monthlyAdSpend: newMonthlyAdSpend
    });
  };
  const handlePushLeft = (monthIndex: number) => {
    if (monthIndex === 0) return;
    const arrayIndex = inputIndexForMonth(monthIndex);
    if (arrayIndex < 0 || arrayIndex > 11) return;
    const currentMonthlyCampaigns = inputs.monthlyCampaigns || Array(12).fill(inputs.campaigns);
    const valueToPropagate = currentMonthlyCampaigns[arrayIndex];
    const currentMonthlyAdSpend = inputs.monthlyAdSpend || Array(12).fill(inputs.adSpend);
    const spendToPropagate = currentMonthlyAdSpend[arrayIndex];
    const newMonthlyCampaigns = currentMonthlyCampaigns.map((val, idx) => {
      return idx <= arrayIndex ? valueToPropagate : val;
    });
    const newMonthlyAdSpend = currentMonthlyAdSpend.map((val, idx) => {
      return idx <= arrayIndex ? spendToPropagate : val;
    });
    onInputsChange({
      ...inputs,
      monthlyCampaigns: newMonthlyCampaigns,
      monthlyAdSpend: newMonthlyAdSpend
    });
  };
  // Total monthly ad spend is stored as (per-campaign spend × campaign tier).
  // Any custom total is allowed; the campaign tier — and therefore package
  // pricing — is derived from $2,500 bands: $1-2,500 = 1x, $2,501-5,000 = 2x,
  // $5,001-7,500 = 3x, and so on.
  const campaignTierForSpend = (total: number) => total <= 0 ? 0 : Math.ceil(total / CAMPAIGN_SPEND_STEP);
  const applyMonthlyTotals = (totalsByIndex: Record<number, number>) => {
    const newMonthlyAdSpend = [...(inputs.monthlyAdSpend || Array(12).fill(inputs.adSpend))];
    const newMonthlyCampaigns = [...(inputs.monthlyCampaigns || Array(12).fill(inputs.campaigns))];
    Object.entries(totalsByIndex).forEach(([key, rawTotal]) => {
      const arrayIndex = Number(key);
      const total = Math.max(0, Math.round(rawTotal));
      const tier = campaignTierForSpend(total);
      newMonthlyCampaigns[arrayIndex] = tier;
      newMonthlyAdSpend[arrayIndex] = tier === 0 ? 0 : total / tier;
    });
    onInputsChange({
      ...inputs,
      monthlyCampaigns: newMonthlyCampaigns,
      monthlyAdSpend: newMonthlyAdSpend
    });
  };
  const getMonthlyTotalSpend = (monthIndex: number) => {
    if (monthIndex === 0) return 0;
    const arrayIndex = inputIndexForMonth(monthIndex);
    if (arrayIndex < 0 || arrayIndex > 11) return 0;
    const currentMonthlyAdSpend = inputs.monthlyAdSpend || Array(12).fill(inputs.adSpend);
    const currentMonthlyCampaigns = inputs.monthlyCampaigns || Array(12).fill(inputs.campaigns);
    return currentMonthlyAdSpend[arrayIndex] * currentMonthlyCampaigns[arrayIndex];
  };
  const handleAdSpendChange = (monthIndex: number, value: number) => {
    if (monthIndex === 0) return;
    const arrayIndex = inputIndexForMonth(monthIndex);
    if (arrayIndex < 0 || arrayIndex > 11) return;
    applyMonthlyTotals({
      [arrayIndex]: Math.max(0, value)
    });
  };
  // Arrows always snap to the next / previous $2,500 tier boundary.
  const stepAdSpend = (monthIndex: number, direction: 1 | -1) => {
    if (monthIndex === 0) return;
    const total = getMonthlyTotalSpend(monthIndex);
    const nextTotal = direction === 1 ? (Math.floor(total / CAMPAIGN_SPEND_STEP) + 1) * CAMPAIGN_SPEND_STEP : Math.max(CAMPAIGN_SPEND_STEP, (Math.ceil(total / CAMPAIGN_SPEND_STEP) - 1) * CAMPAIGN_SPEND_STEP);
    handleAdSpendChange(monthIndex, nextTotal);
  };
  const handlePushAdSpendLeft = (monthIndex: number) => {
    if (monthIndex === 0) return;
    const arrayIndex = inputIndexForMonth(monthIndex);
    if (arrayIndex < 0 || arrayIndex > 11) return;
    const totalToPropagate = getMonthlyTotalSpend(monthIndex);
    const totals: Record<number, number> = {};
    for (let idx = 0; idx <= arrayIndex; idx++) {
      totals[idx] = totalToPropagate;
    }
    applyMonthlyTotals(totals);
  };
  const handlePushAdSpendRight = (monthIndex: number) => {
    if (monthIndex === 0) return;
    const arrayIndex = inputIndexForMonth(monthIndex);
    if (arrayIndex < 0 || arrayIndex > 11) return;
    const totalToPropagate = getMonthlyTotalSpend(monthIndex);
    const totals: Record<number, number> = {};
    for (let idx = arrayIndex; idx < 12; idx++) {
      totals[idx] = totalToPropagate;
    }
    applyMonthlyTotals(totals);
  };
  // Milestone bonus rep counts per month: day 15 lands in the launch month,
  // day 45 the following month, and day 90 three months after launch.
  const getBonusMilestones = (rowIdx: number) => {
    const rowAt = (idx: number) => idx >= 1 && idx < displayRows.length ? displayRows[idx] : undefined;
    const current = rowAt(rowIdx);
    const prior = rowAt(rowIdx - 1);
    const pct30 = inputs.pct30 / 100;
    const pct60 = Math.sqrt(inputs.pct90 / 100);
    // Each bonus counts the reps who reach the next milestone, rounded up.
    const day15Reps = current ? Math.ceil(current.launched * pct30) : 0;
    const day45Reps = prior ? Math.ceil(prior.launched * pct30 * pct60) : 0;
    const day90Reps = current ? Math.round(current.active90d) : 0;
    const enabled = !!inputs.pkg && inputs.salesSchoolEnabled !== false;
    // While the leader is active, a flat monthly fee is billed here too.
    const leaderActive = !!inputs.pkg && !!inputs.leaderCampaign && rowIdx > LEADER_CAMPAIGN_MONTHS ? LEADER_ACTIVE_MONTHLY_FEE : 0;
    return {
      day15Reps: enabled ? day15Reps : 0,
      day45Reps: enabled ? day45Reps : 0,
      day90Reps: enabled ? day90Reps : 0,
      day15: enabled ? day15Reps * BONUS_DAY_15 : 0,
      day45: enabled ? day45Reps * BONUS_DAY_45 : 0,
      day90: enabled ? day90Reps * BONUS_DAY_90 : 0,
      leaderActive
    };
  };
  // Month 2 is the leader rep's first selling month.
  const isLeaderSalesMonth = (monthIndex: number) => !!inputs.leaderCampaign && monthIndex === 2;
  const saveLeaderSalesEdit = () => {
    const parsed = parseFloat(editLeaderSalesValue);
    setEditingLeaderSales(false);
    setEditLeaderSalesValue('');
    if (!Number.isNaN(parsed)) {
      onInputsChange({
        ...inputs,
        leaderMonth2Sales: Math.max(0, parsed)
      });
    }
  };
  const handleLeaderSalesKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveLeaderSalesEdit();
    } else if (e.key === 'Escape') {
      setEditingLeaderSales(false);
      setEditLeaderSalesValue('');
    }
  };
  const startEditingAdSpend = (monthIndex: number, currentValue: number) => {
    if (monthIndex === 0 || isLeaderMonth(monthIndex)) return;
    setEditingAdSpend(monthIndex);
    setEditAdSpendValue(Math.round(currentValue).toString());
  };
  const saveAdSpendEdit = () => {
    if (editingAdSpend !== null) {
      const value = parseFloat(editAdSpendValue);
      if (!Number.isNaN(value)) {
        handleAdSpendChange(editingAdSpend, value);
      }
      setEditingAdSpend(null);
      setEditAdSpendValue('');
    }
  };
  const cancelAdSpendEdit = () => {
    setEditingAdSpend(null);
    setEditAdSpendValue('');
  };
  const handleAdSpendKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveAdSpendEdit();
    } else if (e.key === 'Escape') {
      cancelAdSpendEdit();
    }
  };
  const startEditingCampaign = (monthIndex: number, currentValue: number) => {
    if (monthIndex === 0) return;
    setEditingCampaign(monthIndex);
    setEditCampaignValue(currentValue.toString());
  };
  const saveCampaignEdit = () => {
    if (editingCampaign !== null) {
      const value = parseFloat(editCampaignValue) || 0;
      handleCampaignChange(editingCampaign, value);
      setEditingCampaign(null);
      setEditCampaignValue('');
    }
  };
  const cancelCampaignEdit = () => {
    setEditingCampaign(null);
    setEditCampaignValue('');
  };
  const handleCampaignKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveCampaignEdit();
    } else if (e.key === 'Escape') {
      cancelCampaignEdit();
    }
  };
  // Handle build fee editing - saves to inputs for persistence
  const startEditingBuildFee = (monthIndex: number, currentValue: number) => {
    setEditingBuildFee(monthIndex);
    setEditBuildFeeValue(currentValue.toString());
  };
  const handleBuildFeeChange = (monthIndex: number, value: number) => {
    // Initialize monthly build fees array if not present
    // Index 0 = Build, Index 1 = Month 1, etc. (13 total slots)
    const currentBuildFees = inputs.monthlyBuildFees || Array(13).fill(undefined);
    const newBuildFees = [...currentBuildFees];
    newBuildFees[monthIndex] = Math.max(0, value);
    onInputsChange({
      ...inputs,
      monthlyBuildFees: newBuildFees
    });
  };
  // Build & implementation payment plans. Index 0 is the Build column, the rest
  // are consecutive months. Clicking the row label cycles upfront → plans → upfront.
  const buildSplitPlans = BUILD_SPLIT_PLANS[inputs.pkg] ?? [];
  const buildFeeTotal = (() => {
    if (!inputs.pkg) return 0;
    return PACKAGE_PRESETS[inputs.pkg].buildUpfront + (inputs.leaderCampaign ? LEADER_CAMPAIGN_BUILD_UPCHARGE : 0) + (inputs.addRecruiter ? RECRUITER_BUILD_UPCHARGE : 0);
  })();
  const activeBuildPlan = (() => {
    const fees = inputs.monthlyBuildFees;
    if (!fees) return 0;
    const paying = fees.filter(fee => fee !== undefined && fee !== null && fee > 0).length;
    const matched = buildSplitPlans.findIndex(plan => plan.length === paying);
    return matched === -1 ? 0 : matched + 1;
  })();
  const cycleBuildPlan = () => {
    if (buildSplitPlans.length === 0) return;
    const nextPlan = activeBuildPlan >= buildSplitPlans.length ? 0 : activeBuildPlan + 1;
    if (nextPlan === 0) {
      onInputsChange({
        ...inputs,
        buildPay: 'upfront',
        monthlyBuildFees: undefined
      });
      return;
    }
    // Payments are always equal: the full implementation fee (including any
    // leader or recruiter upcharges) is divided evenly across the plan.
    const plan = buildSplitPlans[nextPlan - 1];
    const payments = plan.length;
    const perPayment = buildFeeTotal / payments;
    const newBuildFees: (number | undefined)[] = Array(13).fill(undefined);
    for (let idx = 0; idx < payments; idx++) {
      // idx 0 is the upfront payment; monthly payments start in Month 2.
      const slot = idx === 0 ? 0 : idx + 1;
      newBuildFees[slot] = perPayment;
    }
    onInputsChange({
      ...inputs,
      buildPay: 'two',
      monthlyBuildFees: newBuildFees as number[]
    });
  };
  const saveBuildFeeEdit = () => {
    if (editingBuildFee !== null) {
      const value = parseFloat(editBuildFeeValue) || 0;
      handleBuildFeeChange(editingBuildFee, value);
      setEditingBuildFee(null);
      setEditBuildFeeValue('');
    }
  };
  const cancelBuildFeeEdit = () => {
    setEditingBuildFee(null);
    setEditBuildFeeValue('');
  };
  const handleBuildFeeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveBuildFeeEdit();
    } else if (e.key === 'Escape') {
      cancelBuildFeeEdit();
    }
  };
  const startEditingPlatformFee = (monthIndex: number, currentValue: number) => {
    setEditingPlatformFee(monthIndex);
    setEditPlatformFeeValue(currentValue.toString());
  };
  const savePlatformFeeEdit = () => {
    if (editingPlatformFee !== null) {
      setEditingPlatformFee(null);
      setEditPlatformFeeValue('');
    }
  };
  const cancelPlatformFeeEdit = () => {
    setEditingPlatformFee(null);
    setEditPlatformFeeValue('');
  };
  const handlePlatformFeeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      savePlatformFeeEdit();
    } else if (e.key === 'Escape') {
      cancelPlatformFeeEdit();
    }
  };
  const startEditingCampaignFee = (monthIndex: number, currentValue: number) => {
    setEditingCampaignFee(monthIndex);
    setEditCampaignFeeValue(currentValue.toString());
  };
  const saveCampaignFeeEdit = () => {
    if (editingCampaignFee !== null) {
      setEditingCampaignFee(null);
      setEditCampaignFeeValue('');
    }
  };
  const cancelCampaignFeeEdit = () => {
    setEditingCampaignFee(null);
    setEditCampaignFeeValue('');
  };
  const handleCampaignFeeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveCampaignFeeEdit();
    } else if (e.key === 'Escape') {
      cancelCampaignFeeEdit();
    }
  };
  // Apply scenario adjustments to rows
  const adjustedRows = useMemo(() => {
    if (!halfSales && !doubleAttrition && revenueShift === 0 && salesDelay === 0) return rows;
    return rows.map((row, idx) => {
      let newRow = {
        ...row
      };
      // Apply sales delay (zero out sales for first N months)
      // Also shift residual revenue when sales are delayed
      if (salesDelay > 0 && idx > 0 && idx <= salesDelay) {
        // Zero out sales and residual for months 1 through salesDelay
        newRow.totalSales = 0;
        newRow.grossRevenue = 0;
        newRow.residualRevenue = 0;
      } else if (salesDelay > 0 && idx > salesDelay) {
        // For months after the delay, shift the residual revenue
        // The residual revenue should come from (idx - salesDelay) position in original rows
        const sourceIdx = idx - salesDelay;
        if (sourceIdx > 0 && sourceIdx < rows.length) {
          newRow.residualRevenue = rows[sourceIdx].residualRevenue || 0;
        } else {
          newRow.residualRevenue = 0;
        }
      }
      // Apply revenue shift (this can further modify grossRevenue and residualRevenue)
      if (revenueShift !== 0) {
        const sourceIdx = idx - revenueShift;
        if (idx > 0 && sourceIdx >= 0 && sourceIdx < rows.length) {
          if (salesDelay === 0 || idx > salesDelay) {
            newRow.totalSales = rows[sourceIdx].totalSales;
          }
          newRow.grossRevenue = rows[sourceIdx].grossRevenue;
          newRow.residualRevenue = rows[sourceIdx].residualRevenue || 0;
        } else if (idx > 0) {
          if (salesDelay === 0 || idx > salesDelay) {
            newRow.totalSales = 0;
          }
          newRow.grossRevenue = 0;
          newRow.residualRevenue = 0;
        }
      }
      // Apply half sales scenario
      if (halfSales) {
        newRow.totalSales = newRow.totalSales * 0.5;
        newRow.grossRevenue = newRow.grossRevenue * 0.5;
        newRow.residualRevenue = (newRow.residualRevenue || 0) * 0.5;
      }
      // Apply double attrition (half retention) scenario
      if (doubleAttrition) {
        newRow.launched = row.launched * 0.5;
        newRow.active30d = row.active30d * 0.5;
        newRow.active60d = row.active60d * 0.5;
        newRow.active90d = row.active90d * 0.5;
        newRow.veterans = row.veterans * 0.5;
        const retentionMultiplier = 0.5;
        newRow.totalSales = newRow.totalSales * retentionMultiplier;
        newRow.grossRevenue = newRow.grossRevenue * retentionMultiplier;
        newRow.residualRevenue = (newRow.residualRevenue || 0) * retentionMultiplier;
      }
      newRow.grossProfit = newRow.grossRevenue * (inputs.gpm / 100);
      newRow.netProfit = newRow.grossProfit - row.totalCost;
      return newRow;
    });
  }, [rows, halfSales, doubleAttrition, revenueShift, salesDelay, inputs.gpm, inputs.ticket]);
  const finalRows = useMemo(() => {
    let cumProfit = adjustedRows[0].cumProfit;
    return adjustedRows.map((row, idx) => {
      if (idx === 0) return row;
      cumProfit += row.netProfit;
      return {
        ...row,
        cumProfit
      };
    });
  }, [adjustedRows]);
  const displayRows = useMemo(() => {
    return finalRows.slice(0, timeframe + 1);
  }, [finalRows, timeframe]);
  const getFormula = (metricKey: string, row: RowData, rowIndex: number): string => {
    const monthNum = rowIndex;
    switch (metricKey) {
      case 'activeCampaigns':
        return `Number of active campaigns for this month.\nAdjusting this will recalculate ad spend and hires.`;
      case 'hires':
        return `Formula: Ad Spend ÷ Cost per Hire\n\nActual: ${fmtMoney(row.adSpend)} ÷ CPH = ${row.hires.toFixed(0)} hires`;
      case 'launched':
        const launchRate = row.hires > 0 ? (row.launched / row.hires * 100).toFixed(0) : 0;
        return `Formula: Hires × Launch %\n\nActual: ${row.hires.toFixed(0)} × ${launchRate}% = ${row.launched.toFixed(1)} launched`;
      case 'active30d':
        if (monthNum === 0) return 'Build period - no active reps yet';
        if (monthNum === 1) return 'Month 1 - reps not yet at 30 days';
        return `Formula: Previous month's launched reps × Day 30 retention\n\nActual: Single cohort at 30-60 days = ${row.active30d.toFixed(1)} reps`;
      case 'active60d':
        if (monthNum <= 2) return monthNum === 0 ? 'Build period' : 'Not enough time elapsed (need 60 days)';
        return `Formula: 2 months ago launched reps × Day 60 retention\n\nActual: Single cohort at 60-90 days = ${row.active60d.toFixed(1)} reps`;
      case 'active90d':
        if (monthNum < 4) return monthNum === 0 ? 'Build period' : 'Building cohort (need 90 days)...';
        if (monthNum === 4) {
          return `Formula: First cohort reaching 90 days\n\nActual: ${row.active90d.toFixed(1)} reps (90-120 days old)`;
        }
        return `Formula: Cumulative count of all reps 90+ days old\n\nActual: ${row.active90d.toFixed(1)} (90-120d cohort) + ${row.veterans.toFixed(1)} (120d+ veterans)\n= ${(row.active90d + row.veterans).toFixed(1)} total`;
      case 'veterans':
        if (monthNum <= 4) return 'No veterans yet (need 120+ days)';
        return `Formula: Previous month's 90d+ reps with attrition + new 120d graduates\n\nActual: ${row.veterans.toFixed(1)} veterans (120+ days old)`;
      case 'teamSize':
        const ts_launched = row.launched * 0.5;
        const ts_30to60 = row.active30d;
        const ts_60to90 = row.active60d;
        const ts_90plus = rowIndex === 4 ? row.active90d : row.active90d + row.veterans;
        return `Formula: Launched (0-30d at 50%) + Active 30d+ + Active 60d+ + Active 90d+\n\nBreakdown:\n• Launched (0-30d): ${ts_launched.toFixed(1)} (50% weight)\n• Active 30d+ (30-60d): ${ts_30to60.toFixed(1)}\n• Active 60d+ (60-90d): ${ts_60to90.toFixed(1)}\n• Active 90d+ (90d+): ${ts_90plus.toFixed(1)}\n\nTotal: ${(ts_launched + ts_30to60 + ts_60to90 + ts_90plus).toFixed(1)}`;
      case 'adSpend':
        const scenarioNote = halfSales || doubleAttrition ? '\n\n⚠️ Scenario active but ad spend unchanged' : '';
        return `Total managed ad spend for the month\n\nActual: ${fmtMoney(row.adSpend)}${scenarioNote}`;
      case 'buildFee':
        if (row.buildFee === 0) return 'No build fee this month';
        return `Formula: One-time implementation fee\n\nActual: ${fmtMoney(row.buildFee)} (${monthNum === 0 ? 'Build' : `Month ${monthNum} payment`})\n\nClick to edit any cell in this row.`;
      case 'platformFee':
        return `Formula: HIRE platform monthly fee\n\nActual: ${fmtMoney(row.platformFee)} (system access & support)`;
      case 'campaignFee':
        return `Formula: ${fmtMoney(PIPELINE_BUCKET_FEE)} per $2,500 bucket of monthly ad spend\n\nActual: ${fmtMoney(row.campaignFee)}`;
      case 'monthlyFees':
        {
          const hasCorporateRecruiter = row.recruiterFee > 0;
          const recruiterBreakdown = hasCorporateRecruiter ? `\nCorporate Recruiter (${Math.round(row.recruiterFee / RECRUITER_MONTHLY_FEE)}x): ${fmtMoney(row.recruiterFee)}` : '';
          const totalMonthlyFees = row.platformFee + row.campaignFee + row.recruiterFee;
          const recruiterFormula = hasCorporateRecruiter ? ` + ${fmtMoney(RECRUITER_MONTHLY_FEE)} per bucket for the corporate recruiter` : '';
          return `Formula: $1,500 fixed partnership fee + ${fmtMoney(PIPELINE_BUCKET_FEE)} per $2,500 bucket of monthly ad spend${recruiterFormula}\n\nPartnership (fixed): ${fmtMoney(row.platformFee)}\nPipeline Management (${Math.round(row.campaignFee / PIPELINE_BUCKET_FEE)}x): ${fmtMoney(row.campaignFee)}${recruiterBreakdown}\nTotal: ${fmtMoney(totalMonthlyFees)}`;
        }
      case 'performanceBonuses':
        return `Formula: ${fmtMoney(BONUS_DAY_15)} at day 15 + ${fmtMoney(BONUS_DAY_45)} at day 45 + ${fmtMoney(BONUS_DAY_90)} at day 90 per active rep\n\nBonuses are always paid in whole increments.\n\nActual: ${fmtMoney(row.bonuses)}\n\nClick the value for the milestone breakdown.`;
      case 'placementBonus':
        {
          return `Formula: ${fmtMoney(BONUS_DAY_15)} at day 15 + ${fmtMoney(BONUS_DAY_45)} at day 45 + ${fmtMoney(BONUS_DAY_90)} at day 90 per active rep\n\nActual: ${fmtMoney(row.bonuses)}`;
        }
      case 'retentionBonus':
        if (monthNum === 0) {
          return 'Build period - no bonuses billed yet';
        }
        const prevMonthForRetention = finalRows[monthNum - 1];
        let bonus30d = 0;
        let bonus60d = 0;
        let bonus90d = 0;
        let explanation = "Formula: Previous month's active reps × $250\n";
        explanation += '(Billed month after hitting milestone)\n';
        explanation += 'Rounded down to nearest $250\n\n';
        if (inputs.pkg === 'advantage') {
          const raw30d = prevMonthForRetention.active30d * 250;
          bonus30d = Math.floor(raw30d / 250) * 250;
          explanation += `30d milestone: ${prevMonthForRetention.active30d.toFixed(1)} active × $250 = ${fmtMoney(bonus30d)}\n`;
          const raw60d = prevMonthForRetention.active60d * 250;
          bonus60d = Math.floor(raw60d / 250) * 250;
          explanation += `60d milestone: ${prevMonthForRetention.active60d.toFixed(1)} active × $250 = ${fmtMoney(bonus60d)}\n`;
          const raw90d = prevMonthForRetention.active90d * 250;
          bonus90d = Math.floor(raw90d / 250) * 250;
          explanation += `90d milestone: ${prevMonthForRetention.active90d.toFixed(1)} active × $250 = ${fmtMoney(bonus90d)}\n`;
        }
        const totalBonus = bonus30d + bonus60d + bonus90d;
        explanation += `Total: ${fmtMoney(totalBonus)}`;
        let retentionBreakdown = `\n\nCOHORT BREAKDOWN:\n`;
        retentionBreakdown += `Billing for milestones hit in ${prevMonthForRetention.period}:\n`;
        if (prevMonthForRetention.active30d > 0) {
          const cohortMonth = monthNum - 2;
          if (cohortMonth >= 1) {
            retentionBreakdown += `• 30d milestone: ${prevMonthForRetention.active30d.toFixed(1)} reps from ${finalRows[cohortMonth].period} cohort\n`;
          }
        }
        if (prevMonthForRetention.active60d > 0) {
          const cohortMonth = monthNum - 3;
          if (cohortMonth >= 1) {
            retentionBreakdown += `• 60d milestone: ${prevMonthForRetention.active60d.toFixed(1)} reps from ${finalRows[cohortMonth].period} cohort\n`;
          }
        }
        if (prevMonthForRetention.active90d > 0) {
          const cohortMonth = monthNum - 4;
          if (cohortMonth >= 1) {
            retentionBreakdown += `• 90d milestone: ${prevMonthForRetention.active90d.toFixed(1)} reps from ${finalRows[cohortMonth].period} cohort\n`;
          }
        }
        const retentionAttritionNote = doubleAttrition ? '\n\n⚠️ 2x Attrition: Active reps reduced by 50%' : '';
        return explanation + retentionBreakdown + retentionAttritionNote;
      case 'bonuses':
        return `Formula: Launch + Day 30 + Day 60 + Day 90 bonuses\n\nActual: ${fmtMoney(row.bonuses)} total bonuses this month`;
      case 'totalCost':
        return `Formula: Sum of all fees, bonuses, and ad spend (rounded)\n\nActual: ${fmtMoney(row.totalCost)}`;
      case 'totalSales':
        const salesNote = halfSales ? '\n\n⚠️ 50% Sales Scenario: Sales reduced by 50%' : '';
        const attrNote = doubleAttrition ? '\n⚠️ 2x Attrition: Active reps reduced by 50%' : '';
        return `Formula: Sales from reps in first 90 days + Veteran sales\n\nActual: Reps 0-90 days making ${inputs.salesFirst90Total} total sales over 90 days + ${row.veterans.toFixed(1)} veterans × ${inputs.salesVetMonthly} sales/month\n= ${row.totalSales.toFixed(1)} total sales${salesNote}${attrNote}`;
      case 'realizationRate':
        return `Formula: Pullthrough / Realization Rate\n\nActual: ${(row.realizationRate * 100).toFixed(0)}% of sales are realized`;
      case 'grossRevenue':
        const revenueNote = halfSales || doubleAttrition ? '\n\n⚠️ Scenario adjustments applied to sales' : '';
        const residualNote = row.residualRevenue && row.residualRevenue > 0 ? `\n\nResidual Revenue: ${fmtMoney(row.residualRevenue)}\nInitial Sales Revenue: ${fmtMoney(row.grossRevenue - row.residualRevenue)}` : '';
        return `Formula: Total Sales × Realization Rate × Avg Ticket${residualNote}\n\nActual: ${row.totalSales.toFixed(1)} × ${(row.realizationRate * 100).toFixed(0)}% × ${fmtMoney(inputs.ticket)}\n= ${fmtMoney(row.grossRevenue)}${revenueNote}`;
      case 'grossProfit':
        const profitNote = halfSales || doubleAttrition ? '\n\n⚠️ Scenario adjustments applied' : '';
        return `Formula: Gross Revenue × Margin %\n\nActual: ${fmtMoney(row.grossRevenue)} × ${inputs.gpm}%\n= ${fmtMoney(row.grossProfit)}${profitNote}`;
      case 'netProfit':
        return `Formula: Gross Profit - Total Investment\n\nActual: ${fmtMoney(row.grossProfit)} - ${fmtMoney(row.totalCost)}\n= ${fmtMoney(row.netProfit)}`;
      case 'cumProfit':
        return `Formula: Sum of all net profits from Build to ${row.period}\n\nActual: Running total = ${fmtMoney(row.cumProfit)}`;
      case 'roi':
        let cumGrossProfit = 0;
        let cumTotalCost = 0;
        for (let i = 0; i <= rowIndex; i++) {
          cumGrossProfit += finalRows[i].grossProfit;
          cumTotalCost += finalRows[i].totalCost;
        }
        const roiPercent = cumTotalCost > 0 ? cumGrossProfit / cumTotalCost * 100 : 0;
        return `Formula: (Gross Profit To Date ÷ Total Investment To Date) × 100\n\nActual: ${fmtMoney(cumGrossProfit)} ÷ ${fmtMoney(cumTotalCost)} × 100\n= ${roiPercent.toFixed(1)}%`;
      case 'higherPartnership':
        return `Subtotal of Build, Platform, Campaign, and Bonus fees`;
      default:
        return 'Calculation details';
    }
  };
  const metricGroups = [{
    id: 'recruiting',
    label: 'Recruiting',
    color: 'bg-slate-600',
    collapsible: true,
    showHeaderData: false,
    metrics: [{
      key: 'adSpendPerCampaign',
      label: 'Ad Spend',
      format: (v: number) => fmtMoney(v),
      centered: true
    }, {
      key: 'avgCPH',
      label: 'Average CPH',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'hires',
      label: 'Hired Reps',
      format: (v: number) => v.toLocaleString(),
      bold: true
    }]
  }, {
    id: 'activeReps',
    label: 'Active Reps',
    color: 'bg-slate-700',
    collapsible: true,
    showHeaderData: false,
    metrics: [{
      key: 'launched',
      label: 'Launched',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'active30d',
      label: 'Active 30d+',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'active60d',
      label: 'Active 60d+',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'active90d',
      label: 'Active 90d+',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'teamSize',
      label: 'Active Reps (Average)',
      format: (v: number) => v.toFixed(1),
      bold: true,
      calculated: true,
      centered: true
    }]
  }, {
    id: 'investment',
    label: 'Hire Partnership',
    color: 'bg-slate-500',
    collapsible: true,
    showHeaderData: false,
    metrics: [...(inputs.pkg ? [{
      key: 'buildFee' as const,
      label: buildSplitPlans.length > 0 ? <button type="button" onClick={cycleBuildPlan} className="font-inherit underline decoration-dotted decoration-gray-400 underline-offset-2 hover:text-amber transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber focus-visible:ring-offset-2 rounded text-left" title={activeBuildPlan === 0 ? `Currently charged in full upfront (${fmtMoney(buildFeeTotal)}). Click to split it into payments.` : activeBuildPlan >= buildSplitPlans.length ? `Currently split into ${buildSplitPlans[activeBuildPlan - 1].length} payments. Click to charge it all upfront.` : `Currently split into ${buildSplitPlans[activeBuildPlan - 1].length} payments. Click to spread it further.`} aria-label="Toggle how the implementation fee is charged" data-id="element-385" data-mp="2ayh2mj1htap5">
                      Implementation Fee
                    </button> : 'Implementation Fee',
      format: (v: number) => fmtMoney(v),
      editable: true,
      clickToEdit: true
    }, {
      key: 'monthlyFees' as const,
      label: 'Monthly Fees',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'performanceBonuses' as const,
      label: <button type="button" onClick={() => onInputsChange({
        ...inputs,
        salesSchoolEnabled: inputs.salesSchoolEnabled === false
      })} className="font-inherit underline decoration-dotted decoration-gray-400 underline-offset-2 hover:text-amber transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber focus-visible:ring-offset-2 rounded" title={inputs.salesSchoolEnabled === false ? 'Restore all bonus charges' : 'Clear all bonus charges'} aria-label={inputs.salesSchoolEnabled === false ? 'Restore all bonus charges' : 'Clear all bonus charges'} data-id="element-386" data-mp="2egwo3iwk3ulb">
                    Bonuses
                  </button>,
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'higherPartnership' as const,
      label: 'HIRE Partnership',
      format: (v: number) => fmtMoney(v),
      bold: true
    }] : [])]
  }, {
    id: 'revenue',
    label: 'Revenue & Profit',
    color: 'bg-slate-800',
    collapsible: true,
    showHeaderData: false,
    metrics: [{
      key: 'totalSales',
      label: <div className="flex items-center justify-between w-full group" data-id="element-387" data-mp="2lpoizfju1rxg">
              <span data-id="element-388" data-mp="2zoxy0bwayfzu">Total Sales</span>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white px-1 rounded shadow-sm border border-gray-200" data-id="element-389" data-mp="2uo9vasypoaps">
                <button onClick={e => {
            e.stopPropagation();
            setSalesDelay(prev => Math.max(0, prev - 1));
          }} className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-amber hover:bg-amber-50 rounded transition-colors text-[10px]" title="Reduce sales delay (enable sales in earlier months)" data-id="element-390" data-mp="evkrz6dmmjvl">
                  ◀
                </button>
                {salesDelay > 0 && <span className="text-[9px] font-mono font-bold text-amber-600" data-id="element-391" data-mp="dhtdqv6w0oi9">
                    {salesDelay}mo
                  </span>}
                <button onClick={e => {
            e.stopPropagation();
            setSalesDelay(prev => Math.min(11, prev + 1));
          }} className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-amber hover:bg-amber-50 rounded transition-colors text-[10px]" title="Delay sales (zero out sales in early months)" data-id="element-392" data-mp="163cjix8q0vpu">
                  ▶
                </button>
              </div>
            </div>,
      format: (v: number) => v.toFixed(1),
      bold: true
    }, {
      key: 'realizationRate',
      label: 'Realization Rate',
      format: (v: number) => `${(v * 100).toFixed(0)}%`
    }, ...(inputs.revenueCadence !== 'one-time' ? [{
      key: 'residualRevenue' as const,
      label: inputs.revenueCadence === 'monthly' ? 'Monthly Residual Revenue' : 'Quarterly Residual Revenue',
      format: (v: number) => fmtMoney(v)
    }] : []), {
      key: 'grossRevenue',
      label: <div className="flex items-center justify-between w-full group" data-id="element-393" data-mp="32hacph52x2vt">
              <span data-id="element-394" data-mp="3q7einv5qpepz">
                {inputs.revenueCadence !== 'one-time' ? 'Total Monthly Revenue' : 'Monthly Gross Revenue'}
              </span>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white px-1 rounded shadow-sm border border-gray-200 relative z-20 mr-1" data-id="element-395" data-mp="87snvu3sb4al">
                <button onClick={e => {
            e.stopPropagation();
            setRevenueShift(prev => prev - 1);
          }} className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-amber hover:bg-amber-50 rounded transition-colors text-[10px]" title="Shift revenue earlier" data-id="element-396" data-mp="hz6hxmcyvbwc">
                  ◀
                </button>
                {revenueShift !== 0 && <span className="text-[9px] font-mono font-bold text-amber-600" data-id="element-397" data-mp="3qe6z3lao505a">
                    {revenueShift > 0 ? '+' : ''}
                    {revenueShift}
                  </span>}
                <button onClick={e => {
            e.stopPropagation();
            setRevenueShift(prev => prev + 1);
          }} className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-amber hover:bg-amber-50 rounded transition-colors text-[10px]" title="Shift revenue later" data-id="element-398" data-mp="15paj7n2omzk6">
                  ▶
                </button>
              </div>
            </div>,
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'grossProfit',
      label: 'Monthly Gross Profit',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'totalCost',
      label: 'Total Investment',
      format: (v: number) => fmtMoney(v),
      bold: true
    }, {
      key: 'netProfit',
      label: 'Monthly Net Profit',
      format: (v: number) => fmtMoney(v),
      highlight: true,
      bold: true
    }, {
      key: 'cumProfit',
      label: 'Profit To Date',
      format: (v: number) => fmtMoney(v),
      highlight: true,
      featured: true
    }, {
      key: 'roi',
      label: 'ROI',
      format: (v: number) => `${Math.round(v)}%`,
      highlight: true,
      bg: 'bg-gray-200'
    }]
  }];
  return <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-6 shadow-lg" data-id="element-399" data-mp="3pxdsd9g16zwi">
      <div className="flex items-center justify-between mb-4" data-id="element-400" data-mp="3lx25rr43ypie">
        <h2 className="text-lg md:text-xl font-bold text-navy" data-id="element-401" data-mp="3lymzjeilm4f7">
          {inputs.companyName ? `Monthly Projections for ${inputs.companyName}` : 'Monthly Projections'}
        </h2>

        <div className="flex items-center gap-3" data-id="element-402" data-mp="377xr2jwiv3f1">
          {/* Timeframe Toggle */}
          <div className="flex gap-1 bg-white border-2 border-gray-200 rounded p-1" data-id="element-403" data-mp="37633extv8x79">
            <button onClick={() => setTimeframe(3)} className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${timeframe === 3 ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-404" data-mp="32axhhz0grij6">
              3M
            </button>
            <button onClick={() => setTimeframe(6)} className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${timeframe === 6 ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-405" data-mp="3rwgsz6ux71jh">
              6M
            </button>
            <button onClick={() => setTimeframe(12)} className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${timeframe === 12 ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-406" data-mp="346mekkuyd4x4">
              12M
            </button>
          </div>

          {/* Scenario Toggles */}
          <button onClick={() => onInputsChange({
          ...inputs,
          pkg: inputs.pkg ? '' : 'advantage'
        })} title="Include HIRE Advantage partnership fees in the projection" className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${inputs.pkg ? 'bg-amber text-navy border-amber' : 'bg-white text-gray-700 border-gray-300 hover:border-amber'}`} data-id="element-407" data-mp="2za4dpw2qtqq2">
            {inputs.pkg ? '✓ ' : ''}Advantage
          </button>
          <button onClick={() => onInputsChange({
          ...inputs,
          addRecruiter: !inputs.addRecruiter
        })} title={`Add a dedicated corporate recruiter: ${fmtMoney(RECRUITER_MONTHLY_FEE)} per $2,500 of ad spend, plus ${fmtMoney(RECRUITER_BUILD_UPCHARGE)} on the implementation fee`} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${inputs.addRecruiter ? 'bg-slate-600 text-white border-slate-700' : 'bg-white text-gray-700 border-gray-300 hover:border-slate-500'}`} data-id="element-408" data-mp="2mxtjzfb9h0a1">
            {inputs.addRecruiter ? '✓ ' : ''}Add Recruiter
          </button>
          <button onClick={() => onInputsChange({
          ...inputs,
          leaderCampaign: !inputs.leaderCampaign
        })} title={`Run a two-month leader search first: +${fmtMoney(LEADER_CAMPAIGN_BUILD_UPCHARGE)} one-time, ${fmtMoney(LEADER_CAMPAIGN_MONTHLY_FEE)}/month while searching, then ${fmtMoney(LEADER_ACTIVE_MONTHLY_FEE)}/month while the leader is active`} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${inputs.leaderCampaign ? 'bg-navy text-white border-navy' : 'bg-white text-gray-700 border-gray-300 hover:border-navy'}`} data-id="element-409" data-mp="3bdb851bmlmp8">
            {inputs.leaderCampaign ? '✓ ' : ''}Leader Campaign
          </button>
          <button onClick={() => setHalfSales(!halfSales)} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${halfSales ? 'bg-red-500 text-white border-red-600' : 'bg-white text-gray-700 border-gray-300 hover:border-red-400'}`} data-id="element-410" data-mp="cucoz21u05kv">
            {halfSales ? '✓ ' : ''}50% Sales Scenario
          </button>
          <button onClick={() => setDoubleAttrition(!doubleAttrition)} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${doubleAttrition ? 'bg-orange-500 text-white border-orange-600' : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'}`} data-id="element-411" data-mp="21eiguk7l7bad">
            {doubleAttrition ? '✓ ' : ''}2x Attrition Scenario
          </button>
          <button onClick={onPlusFive} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${plusFive ? 'bg-blue-500 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-600'} active:translate-y-[1px]`} data-id="element-412" data-mp="2l2j01dg4ow3y">
            {plusFive ? '✓ ' : ''}+5%
          </button>
          <button onClick={onToggleOnTarget} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${onTarget ? 'bg-emerald-500 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-400'}`} data-id="element-413" data-mp="341wtzvduy6ug">
            {onTarget ? '✓ ' : ''}On Target
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded border-2 border-gray-200" data-id="element-414" data-mp="22e1k4yoik2nn">
        <table className="w-full border-collapse text-sm" style={{
        tableLayout: 'fixed'
      }} data-id="element-415" data-mp="27fihwlbpb900">
          <thead className="sticky top-0 z-20" data-id="element-416" data-mp="303efrtd0b6yd">
            <tr className="bg-navy text-white" data-id="element-417" data-mp="3034nfz4honpi">
              <th className="bg-navy text-xs font-extrabold uppercase tracking-wide p-2 text-left border-r border-gray-300" style={{
              width: '170px'
            }} data-id="element-418" data-mp="2m459pmiilt84">
                <span className="inline-block" data-id="element-419" data-mp="1anngdva7s6pp">Metric</span>
              </th>
              {displayRows.map((row, idx) => {
              const displayPeriod = row.period === 'Build' ? 'Build' : row.period.replace('Month', 'Mo');
              const isQuarterEnd = idx > 0 && idx % 3 === 0;
              const isColHighlighted = hoveredCell !== null && hoveredCell.colIdx === idx;
              return <th key={row.period} className={`text-xs font-extrabold uppercase tracking-wide p-2 text-center whitespace-nowrap border-r border-gray-300 transition-colors ${isQuarterEnd ? 'border-r-4' : ''} ${isColHighlighted ? 'bg-navy/80 text-amber' : 'bg-navy'}`} data-id="element-420" data-mp="1c6x4s7qmzug1" data-mpi={idx}>
                    {displayPeriod}
                  </th>;
            })}
            </tr>
          </thead>
          <tbody className="bg-white" data-id="element-421" data-mp="tdjewh1s6e7a">
            {metricGroups.filter(group => group.metrics.length > 0).map((group, groupIdx) => {
            const isExpanded = group.collapsible ? expandedSections[group.id as keyof typeof expandedSections] : true;
            const showDetails = !group.collapsible || isExpanded;
            return <Fragment key={group.id} data-id="element-422" data-mp="2mmj3h75rivrx" data-mpi={groupIdx}>
                  {!group.noHeader && <tr className={`${group.color}`} data-id="element-423" data-mp="2mkg0znnfba6b">
                      <td className={`${group.color} p-2 font-extrabold text-white text-xs border-r border-gray-300 ${group.collapsible ? 'cursor-pointer hover:bg-opacity-80' : ''}`} onClick={() => group.collapsible && toggleSection(group.id as keyof typeof expandedSections)} data-id="element-424" data-mp="2hp0q94kppue4">
                        <div className="flex items-center gap-2" data-id="element-425" data-mp="1tydeg87oekma">
                          {group.collapsible && <span className="text-white" data-id="element-426" data-mp="1oy8w0cn7jftf">
                              {isExpanded ? '▼' : '▶'}
                            </span>}
                          <span className="uppercase tracking-wide text-xs inline-block" data-id="element-427" data-mp="9fb3zgzm9j04">
                            {group.label}
                          </span>
                        </div>
                      </td>
                      {displayRows.map((row, rowIdx) => {
                  const isQuarterEnd = rowIdx > 0 && rowIdx % 3 === 0;
                  return <td key={row.period} className={`${group.color} p-2 text-center font-extrabold text-white border-r border-gray-300 ${isQuarterEnd ? 'border-r-4' : ''}`} data-id="element-428" data-mp="s613qwtw4por" data-mpi={rowIdx}></td>;
                })}
                    </tr>}

                  {showDetails && group.metrics.map((metric, metricIdx) => {
                const isLastInGroup = metricIdx === group.metrics.length - 1;
                const isRowHighlighted = hoveredMetricKey === metric.key;
                const isCrosshairRow = hoveredCell !== null && hoveredCell.metricKey === metric.key;
                return <tr key={metric.key} className={`h-10 ${metric.featured ? 'bg-amber-light' : 'bg-white'}`} data-id="element-429" data-mp="30sxb9ee8uqkg" data-mpi={metricIdx}>
                          <td className={`p-2 ${group.noHeader ? '' : 'pl-4'} text-gray-900 ${metric.bold || metric.featured ? 'font-extrabold text-xs' : 'font-bold text-xs'} border-r border-gray-300 transition-colors cursor-pointer whitespace-nowrap relative ${isRowHighlighted || isCrosshairRow ? 'bg-amber-50' : metric.featured ? 'bg-amber-light' : 'bg-white'}`} onMouseEnter={() => {
                    setHoveredMetricKey(metric.key);
                    setHoveredCell(null);
                  }} onMouseLeave={() => setHoveredMetricKey(null)} data-id="element-430" data-mp="3arwnt824r6cq">
                            <span className="inline-block" data-id="element-431" data-mp="70ce4nndkqqg">{metric.label}</span>
                          </td>
                          {displayRows.map((row, rowIdx) => {
                    let value: number;
                    let displayValue: React.ReactNode;
                    // Milestone bonuses are computed by the model.
                    const getPlacementBonus = (r: RowData, _idx: number) => r.bonuses;
                    const getRetentionBonus = () => 0;
                    const getTotalAdSpend = (idx: number) => {
                      if (idx === 0) return 0;
                      return row.adSpend;
                    };
                    const getAvgCPH = (idx: number) => {
                      if (idx === 0) return 0;
                      // Leader campaign months use their own blended CPH.
                      if (isLeaderMonth(idx)) return LEADER_CAMPAIGN_CPH;
                      return inputs.cph;
                    };
                    const getBuildFeeValue = (idx: number) => {
                      // No package selected → ignore any stored build fees
                      if (!inputs.pkg) return 0;
                      const customBuildFees = inputs.monthlyBuildFees;
                      if (customBuildFees && customBuildFees[idx] !== undefined && customBuildFees[idx] !== null) {
                        return customBuildFees[idx];
                      }
                      return row.buildFee;
                    };
                    if (metric.key === 'teamSize') {
                      const launched0to30 = row.launched * 0.5;
                      const active30to60 = row.active30d;
                      const active60to90 = row.active60d;
                      const active90plus = row.active90d + row.veterans;
                      value = launched0to30 + active30to60 + active60to90 + active90plus;
                    } else if (metric.key === 'active30d') {
                      value = row.active30d;
                    } else if (metric.key === 'active60d') {
                      value = row.active60d;
                    } else if (metric.key === 'active90d') {
                      if (rowIdx === 4) {
                        value = row.active90d;
                      } else if (rowIdx > 4) {
                        value = row.active90d + row.veterans;
                      } else {
                        value = 0;
                      }
                    } else if (metric.key === 'adSpendPerCampaign') {
                      value = getTotalAdSpend(rowIdx);
                    } else if (metric.key === 'avgCPH') {
                      value = getAvgCPH(rowIdx);
                    } else if (metric.key === 'placementBonus') {
                      value = getPlacementBonus(row, rowIdx);
                    } else if (metric.key === 'retentionBonus') {
                      value = getRetentionBonus(row, rowIdx);
                    } else if (metric.key === 'performanceBonuses') {
                      value = getPlacementBonus(row, rowIdx) + getRetentionBonus(row, rowIdx);
                    } else if (metric.key === 'buildFee') {
                      value = getBuildFeeValue(rowIdx);
                    } else if (metric.key === 'monthlyFees') {
                      value = row.platformFee + row.campaignFee + row.recruiterFee;
                    } else if (metric.key === 'higherPartnership') {
                      const pBonus = getPlacementBonus(row, rowIdx);
                      const rBonus = getRetentionBonus(row, rowIdx);
                      const buildFeeVal = getBuildFeeValue(rowIdx);
                      value = buildFeeVal + row.platformFee + row.campaignFee + row.recruiterFee + row.leaderRevShare + pBonus + rBonus;
                    } else if (metric.key === 'totalCost') {
                      if (!inputs.pkg) {
                        // No package selected: only ad spend contributes
                        value = row.adSpend;
                      } else {
                        const pBonus = getPlacementBonus(row, rowIdx);
                        const rBonus = getRetentionBonus(row, rowIdx);
                        const buildFeeVal = getBuildFeeValue(rowIdx);
                        const subtotal = buildFeeVal + row.platformFee + row.campaignFee + row.recruiterFee + row.leaderRevShare + pBonus + rBonus;
                        value = subtotal + row.adSpend;
                      }
                    } else if (metric.key === 'roi') {
                      let cumGrossProfit = 0;
                      let cumTotalCost = 0;
                      for (let i = 0; i <= rowIdx; i++) {
                        cumGrossProfit += finalRows[i].grossProfit;
                        cumTotalCost += finalRows[i].totalCost;
                      }
                      value = cumTotalCost > 0 ? cumGrossProfit / cumTotalCost * 100 : 0;
                    } else if (metric.key === 'netProfit' && !inputs.pkg) {
                      // No package: net profit = gross profit − ad spend only
                      value = row.grossProfit - row.adSpend;
                    } else if (metric.key === 'cumProfit' && !inputs.pkg) {
                      // No package: profit-to-date sums (grossProfit − adSpend) across rows
                      let cum = 0;
                      for (let i = 0; i <= rowIdx; i++) {
                        cum += finalRows[i].grossProfit - finalRows[i].adSpend;
                      }
                      value = cum;
                    } else {
                      value = row[metric.key as keyof RowData] as number;
                    }
                    const formatted = metric.format(value);
                    if (metric.key === 'activeCampaigns' && metric.editable && rowIdx > 0 && !isLeaderMonth(rowIdx)) {
                      if (editingCampaign === rowIdx) {
                        displayValue = <input type="number" min="0" step="0.5" value={editCampaignValue} onChange={e => setEditCampaignValue(e.target.value)} onKeyDown={handleCampaignKeyDown} onBlur={saveCampaignEdit} autoFocus className="w-20 text-center border-2 border-amber rounded p-1 text-xs font-semibold focus:border-amber outline-none bg-white" data-id="element-432" data-mp="3oo17y9tdjplg" />;
                      } else {
                        displayValue = <div className="relative flex items-center justify-center group/cell" data-id="element-433" data-mp="2oka1wdghpw3w">
                                    {rowIdx > 1 && <button onClick={e => {
                            e.stopPropagation();
                            handlePushLeft(rowIdx);
                          }} title={`← Copy ${formatted} to all earlier months`} className="absolute left-1 opacity-0 group-hover/cell:opacity-100 transition-opacity text-gray-400 hover:text-amber active:scale-95 transition-all" data-id="element-434" data-mp="2fkwtlo43rdkr">
                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" data-id="element-435" data-mp="1tec8nszezuvl">
                                          <path d="M11 7H3M6 4L3 7l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-436" data-mp="1ybly38mt06jf" />
                                        </svg>
                                      </button>}
                                    <span onClick={() => startEditingCampaign(rowIdx, value)} className="cursor-pointer hover:text-amber transition-colors" data-id="element-437" data-mp="mouap2kl4idk">
                                      {formatted}
                                    </span>
                                    {rowIdx < displayRows.length - 1 && <button onClick={e => {
                            e.stopPropagation();
                            handlePushRight(rowIdx);
                          }} title={`Copy ${formatted} to all months from here →`} className="absolute right-1 opacity-0 group-hover/cell:opacity-100 transition-opacity text-gray-400 hover:text-amber active:scale-95 transition-all" data-id="element-438" data-mp="2r7bugv8h1cob">
                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" data-id="element-439" data-mp="24ywollsii1v2">
                                          <path d="M3 7h8M8 4l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-440" data-mp="1qzeyn33hcik4" />
                                        </svg>
                                      </button>}
                                  </div>;
                      }
                    } else if (metric.key === 'activeCampaigns' && isLeaderMonth(rowIdx)) {
                      // Leader pilot months aren't a campaign multiple.
                      displayValue = 'Leader';
                    } else if (metric.key === 'activeCampaigns' && rowIdx === 0) {
                      displayValue = formatted;
                    } else if (metric.key === 'buildFee' && metric.clickToEdit) {
                      if (editingBuildFee === rowIdx) {
                        displayValue = <input type="number" min="0" step="100" value={editBuildFeeValue} onChange={e => setEditBuildFeeValue(e.target.value)} onKeyDown={handleBuildFeeKeyDown} onBlur={saveBuildFeeEdit} autoFocus className="w-24 text-center border-2 border-amber rounded p-1 text-xs font-semibold focus:border-amber outline-none bg-white" data-id="element-441" data-mp="1qamvbwjrs9rv" />;
                      } else {
                        displayValue = <span onClick={() => startEditingBuildFee(rowIdx, value)} className="cursor-pointer hover:text-amber transition-colors" data-id="element-442" data-mp="31s937279adkd">
                                    {formatted}
                                  </span>;
                      }
                    } else if (metric.key === 'platformFee' && metric.clickToEdit) {
                      if (editingPlatformFee === rowIdx) {
                        displayValue = <input type="number" min="0" step="100" value={editPlatformFeeValue} onChange={e => setEditPlatformFeeValue(e.target.value)} onKeyDown={handlePlatformFeeKeyDown} onBlur={savePlatformFeeEdit} autoFocus className="w-24 text-center border-2 border-amber rounded p-1 text-xs font-semibold focus:border-amber outline-none bg-white" data-id="element-443" data-mp="2ya33fdq7feo3" />;
                      } else {
                        displayValue = <span onClick={() => startEditingPlatformFee(rowIdx, value)} className="cursor-pointer hover:text-amber transition-colors" data-id="element-444" data-mp="dyhfzqs1owns">
                                    {formatted}
                                  </span>;
                      }
                    } else if (metric.key === 'campaignFee' && metric.clickToEdit) {
                      if (editingCampaignFee === rowIdx) {
                        displayValue = <input type="number" min="0" step="100" value={editCampaignFeeValue} onChange={e => setEditCampaignFeeValue(e.target.value)} onKeyDown={handleCampaignFeeKeyDown} onBlur={saveCampaignFeeEdit} autoFocus className="w-24 text-center border-2 border-amber rounded p-1 text-xs font-semibold focus:border-amber outline-none bg-white" data-id="element-445" data-mp="1rrpdwpdx53s3" />;
                      } else {
                        displayValue = <span onClick={() => startEditingCampaignFee(rowIdx, value)} className="cursor-pointer hover:text-amber transition-colors" data-id="element-446" data-mp="6956ewhbqame">
                                    {formatted}
                                  </span>;
                      }
                    } else if (metric.key === 'adSpendPerCampaign' && rowIdx > 0 && !isLeaderMonth(rowIdx) && editingAdSpend === rowIdx) {
                      displayValue = <input type="number" min="0" step="100" value={editAdSpendValue} onChange={e => setEditAdSpendValue(e.target.value)} onKeyDown={handleAdSpendKeyDown} onBlur={saveAdSpendEdit} autoFocus className="w-24 text-center border-2 border-amber rounded p-1 text-xs font-semibold focus:border-amber outline-none bg-white" data-id="element-447" data-mp="16fqteae0j6sm" />;
                    } else if (metric.key === 'adSpendPerCampaign' && rowIdx > 0 && !isLeaderMonth(rowIdx)) {
                      displayValue = <div className="relative grid h-12 grid-rows-[12px_1fr_12px] items-center justify-center group/cell" data-id="element-448" data-mp="2bgicj1m5z3as">
                                  <button type="button" onClick={e => {
                          e.stopPropagation();
                          stepAdSpend(rowIdx, 1);
                        }} title="Increase to the next $2,500 tier" aria-label="Increase to the next $2,500 tier" className="row-start-1 justify-self-center leading-3 opacity-0 group-hover/cell:opacity-100 group-focus-within/cell:opacity-100 text-gray-400 hover:text-amber active:scale-95 transition-all" data-id="element-449" data-mp="2lc24xe1irjus">
                                    ↑
                                  </button>
                                  {value > CAMPAIGN_SPEND_STEP && <button type="button" onClick={e => {
                          e.stopPropagation();
                          stepAdSpend(rowIdx, -1);
                        }} title="Decrease to the previous $2,500 tier" aria-label="Decrease to the previous $2,500 tier" className="row-start-3 justify-self-center leading-3 opacity-0 group-hover/cell:opacity-100 group-focus-within/cell:opacity-100 text-gray-400 hover:text-amber active:scale-95 transition-all" data-id="element-450" data-mp="en89chnwz376">
                                      ↓
                                    </button>}
                                  {rowIdx > 1 && <button type="button" onClick={e => {
                          e.stopPropagation();
                          handlePushAdSpendLeft(rowIdx);
                        }} title={`← Copy ${formatted} to all earlier months`} aria-label="Copy this ad spend to all earlier months" className="absolute left-0 top-1/2 -translate-y-1/2 opacity-0 group-hover/cell:opacity-100 group-focus-within/cell:opacity-100 text-gray-400 hover:text-amber active:scale-95 transition-all" data-id="element-451" data-mp="1ojwx4hxyuhma">
                                      ←
                                    </button>}
                                  <span onClick={() => startEditingAdSpend(rowIdx, value)} title="Click to type a custom monthly ad spend" className="row-start-2 self-center cursor-pointer hover:text-amber transition-colors" data-id="element-452" data-mp="2v1bnqvdre9zw">
                                    {formatted}
                                  </span>
                                  {rowIdx < displayRows.length - 1 && <button type="button" onClick={e => {
                          e.stopPropagation();
                          handlePushAdSpendRight(rowIdx);
                        }} title={`Copy ${formatted} to all later months →`} aria-label="Copy this ad spend to all later months" className="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover/cell:opacity-100 group-focus-within/cell:opacity-100 text-gray-400 hover:text-amber active:scale-95 transition-all" data-id="element-453" data-mp="2h6etyxy9299m">
                                      →
                                    </button>}
                                </div>;
                    } else if (metric.key === 'adSpendPerCampaign' && (rowIdx === 0 || isLeaderMonth(rowIdx))) {
                      displayValue = formatted;
                    } else if (metric.key === 'monthlyFees' && value > 0) {
                      displayValue = <span onClick={e => {
                        e.stopPropagation();
                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                        setFeePopup({
                          rowIdx,
                          platformFee: row.platformFee,
                          campaignFee: row.campaignFee,
                          recruiterFee: row.recruiterFee,
                          x: rect.left + rect.width / 2,
                          y: rect.bottom + 6
                        });
                      }} className="cursor-pointer underline decoration-dotted decoration-gray-400 underline-offset-2 hover:text-amber transition-colors" data-id="element-454" data-mp="3cegqby5eh5h5">
                                  {formatted}
                                </span>;
                    } else if (metric.key === 'performanceBonuses' && value > 0) {
                      const milestones = getBonusMilestones(rowIdx);
                      displayValue = <span onClick={e => {
                        e.stopPropagation();
                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                        setBonusPopup({
                          rowIdx,
                          ...milestones,
                          x: rect.left + rect.width / 2,
                          y: rect.bottom + 6
                        });
                      }} className="cursor-pointer underline decoration-dotted decoration-gray-400 underline-offset-2 hover:text-amber transition-colors" data-id="element-455" data-mp="33eiomsxdjol9">
                                  {formatted}
                                </span>;
                    } else if (metric.key === 'totalSales' && isLeaderSalesMonth(rowIdx) && editingLeaderSales) {
                      displayValue = <input type="number" min="0" step="0.5" value={editLeaderSalesValue} onChange={e => setEditLeaderSalesValue(e.target.value)} onKeyDown={handleLeaderSalesKeyDown} onBlur={saveLeaderSalesEdit} autoFocus className="w-20 text-center border-2 border-amber rounded p-1 text-xs font-semibold focus:border-amber outline-none bg-white" data-id="element-456" data-mp="3oqu7znjaypl9" />;
                    } else if (metric.key === 'totalSales' && isLeaderSalesMonth(rowIdx)) {
                      displayValue = <span onClick={e => {
                        e.stopPropagation();
                        setEditLeaderSalesValue(value.toFixed(1));
                        setEditingLeaderSales(true);
                      }} title="Click to set Month 2 sales for the leader campaign" className="cursor-pointer underline decoration-dotted decoration-amber underline-offset-2 hover:text-amber transition-colors" data-id="element-457" data-mp="3juyh8t9gq85a">
                                  {formatted}
                                </span>;
                    } else if (metric.key === 'totalSales' && isLeaderMonth(rowIdx)) {
                      // No sales are projected during the leader pilot's
                      // first month, so there's nothing to explain.
                      displayValue = formatted;
                    } else if (metric.key === 'totalSales' && rowIdx > 0) {
                      displayValue = <span onClick={e => {
                        e.stopPropagation();
                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                        // Scale the rep counts so the two lines
                        // always add up to the number shown.
                        const rampRate = inputs.salesFirst90Total / 3;
                        const vetRate = inputs.salesVetMonthly;
                        const baseTotal = row.rampingReps * rampRate + row.veteranReps * vetRate;
                        const factor = baseTotal > 0 ? value / baseTotal : 0;
                        setSalesPopup({
                          rowIdx,
                          period: row.period,
                          totalSales: value,
                          rampingReps: row.rampingReps * factor,
                          veterans: row.veteranReps * factor,
                          x: rect.left + rect.width / 2,
                          y: rect.bottom + 6
                        });
                      }} title="Click to see how this number is calculated" className="cursor-pointer underline decoration-dotted decoration-gray-400 underline-offset-2 hover:text-amber transition-colors" data-id="element-458" data-mp="1gjz13d2d58tt">
                                  {formatted}
                                </span>;
                    } else {
                      displayValue = formatted;
                    }
                    const isQuarterEnd = rowIdx > 0 && rowIdx % 3 === 0;
                    let cellClass = `p-2 text-center whitespace-nowrap relative text-sm font-semibold border-r border-gray-300 hover:bg-gray-100 transition-colors ${isQuarterEnd ? 'border-r-4' : ''} ${isRowHighlighted ? 'bg-amber-50' : metric.featured ? 'bg-amber-light' : 'bg-white'}`;
                    if (metric.highlight) {
                      if (value >= 0) {
                        cellClass += ' text-green-600 font-extrabold';
                      } else {
                        cellClass += ' text-red-600 font-extrabold';
                      }
                    } else if (metric.bold) {
                      cellClass += ' font-extrabold text-gray-900';
                    } else {
                      cellClass += ' text-gray-800';
                    }
                    return <td key={row.period} className={cellClass} onMouseEnter={() => {
                      setHoveredCell({
                        metricKey: metric.key,
                        colIdx: rowIdx
                      });
                      setHoveredMetricKey(null);
                    }} onMouseLeave={() => setHoveredCell(null)} data-id="element-459" data-mp="1w2z23pkdkqqk" data-mpi={rowIdx}>
                                {displayValue}
                              </td>;
                  })}
                        </tr>;
              })}
                </Fragment>;
          })}
          </tbody>
        </table>
      </div>

      {tooltip && <div className="fixed z-50 bg-navy text-white p-3 rounded shadow-xl border-2 border-amber max-w-sm text-xs whitespace-pre-line pointer-events-none" style={{
      left: tooltip.x,
      top: tooltip.y
    }} data-id="element-460" data-mp="hx2o5my16ap1">
          {tooltip.content}
        </div>}

      {feePopup && <>
          {/* Click-outside backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setFeePopup(null)} data-id="element-461" data-mp="35tsaw3jbt3g" />
          <div className="fixed z-50 bg-white text-gray-900 rounded shadow-xl border-2 border-amber p-3 text-xs" style={{
        left: feePopup.x,
        top: feePopup.y,
        transform: 'translateX(-50%)',
        minWidth: 200
      }} data-id="element-462" data-mp="3qcmdiavkz8yt">
            <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200" data-id="element-463" data-mp="1jlwp9b1yac4x">
              <span className="font-bold text-navy" data-id="element-464" data-mp="sgkb6k2no2ws">
                Monthly Fees Breakdown
              </span>
              <button onClick={() => setFeePopup(null)} className="text-gray-400 hover:text-gray-700 ml-3 leading-none" aria-label="Close" data-id="element-465" data-mp="xgghipyts6fi">
                ×
              </button>
            </div>
            <div className="space-y-1.5" data-id="element-466" data-mp="9qm2q2uy47sm">
              <div className="flex items-center justify-between gap-4" data-id="element-467" data-mp="9pjbfttpo3c6">
                <span className="text-gray-600" data-id="element-468" data-mp="drxsq9hqev1c">Partnership (fixed)</span>
                <span className="font-semibold text-gray-900" data-id="element-469" data-mp="dpuwv8twe7op">
                  {fmtMoney(feePopup.platformFee)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4" data-id="element-470" data-mp="4sasvs4nhxae">
                <span className="text-gray-600" data-id="element-471" data-mp="1u5xulq050ur8">
                  Pipeline Management (
                  {Math.round(feePopup.campaignFee / PIPELINE_BUCKET_FEE)}x)
                </span>
                <span className="font-semibold text-gray-900" data-id="element-472" data-mp="3jnbggwe54exq">
                  {fmtMoney(feePopup.campaignFee)}
                </span>
              </div>
              {feePopup.recruiterFee > 0 && <div className="flex items-center justify-between gap-4" data-id="element-473" data-mp="3td4k9b81jprf">
                  <span className="text-gray-600" data-id="element-474" data-mp="3tf7iujn9er7r">
                    Corporate Recruiter (
                    {Math.round(feePopup.recruiterFee / RECRUITER_MONTHLY_FEE)}x)
                  </span>
                  <span className="font-semibold text-gray-900" data-id="element-475" data-mp="18k40tk207nn">
                    {fmtMoney(feePopup.recruiterFee)}
                  </span>
                </div>}
              <div className="flex items-center justify-between gap-4 pt-1.5 mt-1.5 border-t border-gray-200" data-id="element-476" data-mp="b78acv2oeybi">
                <span className="font-bold text-navy" data-id="element-477" data-mp="2hxfvceb88gnz">Total</span>
                <span className="font-bold text-navy" data-id="element-478" data-mp="2cz4kc0974046">
                  {fmtMoney(feePopup.platformFee + feePopup.campaignFee + feePopup.recruiterFee)}
                </span>
              </div>
            </div>
          </div>
        </>}

      {bonusPopup && <>
          <div className="fixed inset-0 z-40" onClick={() => setBonusPopup(null)} data-id="element-479" data-mp="1rld235wglbf" />
          <div className="fixed z-50 bg-white text-gray-900 rounded shadow-xl border-2 border-amber p-3 text-xs" style={{
        left: bonusPopup.x,
        top: bonusPopup.y,
        transform: 'translateX(-50%)',
        minWidth: 200
      }} data-id="element-480" data-mp="3tve5hw6j79zh">
            <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200" data-id="element-481" data-mp="3aycofczpdjf6">
              <span className="font-bold text-navy" data-id="element-482" data-mp="1d8l0olhaybz4">Bonuses Breakdown</span>
              <button onClick={() => setBonusPopup(null)} className="text-gray-400 hover:text-gray-700 ml-3 leading-none" aria-label="Close" data-id="element-483" data-mp="1d6h0adim3v3l">
                ×
              </button>
            </div>
            <div className="space-y-1.5" data-id="element-484" data-mp="2w2o0ps0jkp0b">
              <div className="flex items-center justify-between gap-4" data-id="element-485" data-mp="2w1ctfi7w0z4g">
                <span className="text-gray-600" data-id="element-486" data-mp="30uxuxk8owb8e">
                  Day 15: {bonusPopup.day15Reps} ×{' '}
                  {fmtMoney(BONUS_DAY_15)}
                </span>
                <span className="font-semibold text-gray-900" data-id="element-487" data-mp="286ifiwlnbqwj">
                  {fmtMoney(bonusPopup.day15)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4" data-id="element-488" data-mp="2d6ofd022kjpp">
                <span className="text-gray-600" data-id="element-489" data-mp="2h4vll0ywq2mn">
                  Day 45: {bonusPopup.day45Reps} ×{' '}
                  {fmtMoney(BONUS_DAY_45)}
                </span>
                <span className="font-semibold text-gray-900" data-id="element-490" data-mp="3smdef5ey9hue">
                  {fmtMoney(bonusPopup.day45)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4" data-id="element-491" data-mp="1h3x0250ubwe">
                <span className="text-gray-600" data-id="element-492" data-mp="1xgula7bn8exx">
                  Day 90: {bonusPopup.day90Reps} ×{' '}
                  {fmtMoney(BONUS_DAY_90)}
                </span>
                <span className="font-semibold text-gray-900" data-id="element-493" data-mp="27gv7q24t7b6c">
                  {fmtMoney(bonusPopup.day90)}
                </span>
              </div>
              {bonusPopup.leaderActive > 0 && <div className="flex items-center justify-between gap-4" data-id="element-494" data-mp="1er61nad8skxv">
                  <span className="text-gray-600" data-id="element-495" data-mp="19t4n92qrh9sq">Leader active</span>
                  <span className="font-semibold text-gray-900" data-id="element-496" data-mp="19qrwq16xtr42">
                    {fmtMoney(bonusPopup.leaderActive)}
                  </span>
                </div>}
              <div className="flex items-center justify-between gap-4 pt-1.5 mt-1.5 border-t border-gray-200" data-id="element-497" data-mp="1jpfxln6sjpkj">
                <span className="font-bold text-navy" data-id="element-498" data-mp="vzdbrotgzfud">Total</span>
                <span className="font-bold text-navy" data-id="element-499" data-mp="r1bpyw4nt2f7">
                  {fmtMoney(bonusPopup.day15 + bonusPopup.day45 + bonusPopup.day90 + bonusPopup.leaderActive)}
                </span>
              </div>
              <p className="text-gray-500" data-id="element-500" data-mp="2tqlixftv41st">
                Bonuses are always paid in whole increments, never partially.
              </p>
            </div>
          </div>
        </>}

      {salesPopup && <>
          <div className="fixed inset-0 z-40" onClick={() => setSalesPopup(null)} data-id="element-501" data-mp="3ledjsww7u6ev" />
          <div className="fixed z-50 bg-white text-gray-900 rounded shadow-xl border-2 border-amber p-3 text-xs" style={{
        left: salesPopup.x,
        top: salesPopup.y,
        transform: 'translateX(-50%)',
        maxWidth: 320,
        minWidth: 260
      }} data-id="element-502" data-mp="3cfs2f52hcxfo">
            <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200" data-id="element-503" data-mp="2607pbkvmpuw4">
              <span className="font-bold text-navy" data-id="element-504" data-mp="2l9us7qg5xbr3">
                How {salesPopup.period} sales are calculated
              </span>
              <button onClick={() => setSalesPopup(null)} className="text-gray-400 hover:text-gray-700 ml-3 leading-none" aria-label="Close" data-id="element-505" data-mp="2ggjj1invoiw0">
                ×
              </button>
            </div>
            <div className="space-y-2 text-gray-600" data-id="element-506" data-mp="346nlhmpbyclf">
              {isLeaderSalesMonth(salesPopup.rowIdx) ? <>
                  <div className="flex items-center justify-between gap-4" data-id="element-507" data-mp="2z71c5ocmhghw">
                    <span className="font-semibold text-navy" data-id="element-508" data-mp="2v5frhgtujxx5">
                      Leader rep sales
                    </span>
                    <span className="font-bold text-navy" data-id="element-509" data-mp="1js3thmkrq9iw">
                      {salesPopup.totalSales.toFixed(1)}
                    </span>
                  </div>
                  <p className="text-gray-500" data-id="element-510" data-mp="1erpko2bcevjt">
                    {inputs.leaderMonth2Sales !== undefined && inputs.leaderMonth2Sales !== null ? 'This month is set manually. Click the number to change it.' : `The single leader rep sells at 75% of a veteran's rate in their first month: 0.75 × ${inputs.salesVetMonthly} sales/mo. They move to a full veteran rate in Months 3 and 4, then stop.`}
                  </p>
                  <p className="text-gray-500" data-id="element-511" data-mp="d0obek3j5wa8">
                    Click the number in the table to override Month 2 sales
                    directly.
                  </p>
                </> : <>
              <div className="flex items-start justify-between gap-4" data-id="element-512" data-mp="mxj9j1vw5unz">
                <span data-id="element-513" data-mp="qzxv3tqy38iy">
                  New reps ramping:{' '}
                  {salesPopup.rampingReps.toFixed(1)} ×{' '}
                  {(inputs.salesFirst90Total / 3).toFixed(1)} sales/mo
                </span>
                <span className="font-semibold text-navy whitespace-nowrap" data-id="element-514" data-mp="2ssvq9w872d72">
                  {(salesPopup.rampingReps * (inputs.salesFirst90Total / 3)).toFixed(1)}
                </span>
              </div>
              <div className="flex items-start justify-between gap-4" data-id="element-515" data-mp="8zuzm4t8odza">
                <span data-id="element-516" data-mp="4xgltz4fg4ev">
                  Veteran reps: {salesPopup.veterans.toFixed(1)} ×{' '}
                  {inputs.salesVetMonthly} sales/mo
                </span>
                <span className="font-semibold text-navy whitespace-nowrap" data-id="element-517" data-mp="iuwt1cujs27o">
                  {(salesPopup.veterans * inputs.salesVetMonthly).toFixed(1)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4 pt-2 border-t border-gray-200" data-id="element-518" data-mp="nn6eogomqg45">
                <span className="font-semibold text-navy" data-id="element-519" data-mp="11mpk31q23dcr">Total sales</span>
                <span className="font-bold text-navy" data-id="element-520" data-mp="woe6qh0jykps">
                  {salesPopup.totalSales.toFixed(1)}
                </span>
              </div>
              <p className="text-gray-500" data-id="element-521" data-mp="1gbhdvzshu5to">
                <span className="font-semibold text-navy" data-id="element-522" data-mp="1l9966ux1vtye">
                  Only reps who eventually reach the 90-day mark are counted
                  here.
                </span>{' '}
                Reps who wash out before Day 90 contribute no sales in this
                model, so these counts are smaller than the Launched and Active
                rows above.
              </p>
              <p className="text-gray-500" data-id="element-523" data-mp="xhvdgnuzlwcw">
                Ramping reps are in their first 90 days, each closing{' '}
                {inputs.salesFirst90Total} sales over that period. Veterans are
                past 90 days and taper with your {inputs.pct1y}% one-year
                retention.
              </p>
              {(halfSales || doubleAttrition) && <p className="font-semibold text-orange-600" data-id="element-524" data-mp="elcdao25vish">
                  A scenario adjustment is applied to these sales.
                </p>}
              </>}
            </div>
          </div>
        </>}
    </div>;
}